import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/layout/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ProductsTab from '@/components/admin/products/ProductsTab';
import GroupsTab from '@/components/admin/products/GroupsTab';
import TagsTab from '@/components/admin/products/TagsTab';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Configure axios defaults
axios.defaults.timeout = 60000; // 60 seconds timeout

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Set up axios interceptors for debugging
  useEffect(() => {
    // Add request interceptor for debugging
    const requestInterceptor = axios.interceptors.request.use((config) => {
      
      // Modern way to set headers with Axios
      if (config.headers) {
        config.headers.set('Accept', 'application/json');
        config.headers.set('Content-Type', 'application/json');
        config.headers.set('Cache-Control', 'no-cache');
      }
      return config;
    }, (error) => {
      console.error('API Request Error:', error);
      return Promise.reject(error);
    });

    // Add response interceptor for debugging
    const responseInterceptor = axios.interceptors.response.use((response) => {
      
      
      // Check if response data is valid
      if (typeof response.data === 'string' && response.data.includes('<!DOCTYPE html>')) {
        console.error('HTML response detected:', response.data.substring(0, 200));
        return Promise.reject(new Error('Server returned HTML instead of JSON'));
      }
      
      return response;
    }, (error) => {
      console.error('API Response Error:', error);
      
      // Check for HTML in error response
      if (error.response && typeof error.response.data === 'string' && 
          error.response.data.includes('<!DOCTYPE html>')) {
        console.error('HTML error response detected');
        error.message = 'Server returned HTML instead of JSON';
      }
      
      return Promise.reject(error);
    });

    // Clean up interceptors when component unmounts
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Load all data on component mount
  useEffect(() => {
    fetchData();
  }, [retryCount]);

  const fetchData = async () => {
    setIsLoading(true);
    setApiError(null);
    
    try {
      // Fetch all data sequentially to avoid race conditions
      await fetchProducts();
      await fetchGroups();
      await fetchTags();
    } catch (error) {
      console.error('Error fetching data:', error);
      setApiError(error instanceof Error ? error.message : 'Failed to fetch data. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      
      setApiError(null);
      
      const response = await axios.get('/api/products', {
        timeout: 60000, // 60 seconds timeout
        validateStatus: (status) => status < 500 // Consider only 500+ as errors
      });
      
      
      
      if (!Array.isArray(response.data)) {
        console.error('Products data is not an array:', response.data);
        setProducts([]);
        setApiError("API did not return a product array. Please check server response.");
        return;
      }
      
      // Process products and fetch tags for each product
      const productsWithTags = await Promise.all(
        response.data.map(async (product: any) => {
          try {
            const tagResponse = await axios.get(`/api/products/${product.id}/tags`);
            
            const productTags = Array.isArray(tagResponse.data) ? 
              tagResponse.data.map((tag: any) => tag.name) : [];
              
            return { ...product, tags: productTags, date: product.created_at };
          } catch (error) {
            console.error(`Error fetching tags for product ${product.id}:`, error);
            return { ...product, tags: [], date: product.created_at };
          }
        })
      );
      
      setProducts(productsWithTags);
    } catch (error: any) {
      console.error('Error fetching products:', error);
      setProducts([]);
      setApiError(error?.message || "Failed to fetch products. Check network connection and server status.");
      toast({
        title: "Error fetching products",
        description: error?.message || "There was a problem fetching the product data.",
        variant: "destructive",
      });
    }
  };

  const fetchGroups = async () => {
    try {
      
      const response = await axios.get('/api/products/groups', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      
      setGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error('Error fetching groups:', error);
      setGroups([]);
      toast({
        title: "Error fetching groups",
        description: error?.message || "There was a problem fetching the group data.",
        variant: "destructive",
      });
    }
  };

  const fetchTags = async () => {
    try {
      
      const response = await axios.get('/api/tags', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      
      
      if (!Array.isArray(response.data)) {
        console.error('Tags data is not an array:', response.data);
        setTags([]);
        return;
      }
      
      setTags(response.data);
    } catch (error: any) {
      console.error('Error fetching tags:', error);
      setTags([]);
      toast({
        title: "Error fetching tags",
        description: error?.message || "There was a problem fetching the tag data.",
        variant: "destructive",
      });
    }
  };

  // Retry all requests
  const handleRetry = () => {
    setApiError(null);
    setRetryCount(prevCount => prevCount + 1);
    toast({
      title: "Retrying...",
      description: "Attempting to reconnect to the API.",
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {apiError && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>API Error</AlertTitle>
            <AlertDescription>
              {apiError}
              <div className="mt-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry}
                >
                  Retry
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        
        <Tabs defaultValue="products">
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="products">Products</TabsTrigger>
              <TabsTrigger value="groups">Groups</TabsTrigger>
              <TabsTrigger value="tags">Tags</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="products">
            <ProductsTab 
              products={products}
              isLoading={isLoading}
              groups={groups}
              tags={tags}
              fetchProducts={fetchProducts}
            />
          </TabsContent>
          
          <TabsContent value="groups">
            <GroupsTab 
              groups={groups}
              fetchGroups={fetchGroups}
            />
          </TabsContent>
          
          <TabsContent value="tags">
            <TagsTab 
              tags={tags}
              fetchTags={fetchTags}
            />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default ProductManagement;
